
import { useToast } from '@/hooks/use-toast';
import { useAuthSession } from '@/hooks/use-auth-session';
import { supabase } from '@/lib/supabase';
import { useQueryClient } from '@tanstack/react-query';
import { saveGooglePlacesPhotos, migrateGooglePhotosToStorage } from '@/lib/place-image-utils';

export function usePlaceFormSubmit(playlistId: string, onClose: () => void) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const session = useAuthSession();

  const findSimilarPlaces = async (name: string, address: string | null) => {
    if (!name) return null;
    
    try {
      const { data, error } = await supabase
        .rpc('find_similar_places', { 
          place_name: name,
          place_address: address || ''
        });
        
      if (error) throw error;
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('Error finding similar places:', error);
      return null;
    }
  };

  const submitForm = async (formData: any, playlist?: any) => {
    if (!session?.user?.id) {
      toast({
        title: "Error",
        description: "You must be logged in to add places",
        variant: "destructive",
      });
      return;
    }

    try {
      // First check if this place already exists
      const existingPlace = await findSimilarPlaces(formData.name, formData.address);
      let originalPlaceId = null;
      
      if (existingPlace) {
        toast({
          title: "Place found",
          description: "This place exists in another collection. Linking to the original entry.",
        });
        originalPlaceId = existingPlace.original_place_id || existingPlace.id;
      }

      // Process photos - ensure all photos are saved to our storage
      let photos = formData.photos || [];
      
      // Check if there are Google photos or external URLs
      const hasExternalPhotos = photos.some((url: string) => 
        url.includes('http') && 
        !url.includes('supabase.co/storage/v1/object/public/place_images')
      );
      
      if (hasExternalPhotos) {
        toast({
          title: "Processing photos",
          description: "Saving photos to secure storage..."
        });
        
        // Use the migrate function to handle all external photos
        const migratedPlace = await migrateGooglePhotosToStorage({
          ...formData,
          photos: photos
        });
        
        photos = migratedPlace.photos;
      }
      
      const placeData = {
        playlist_id: playlistId,
        place_type: formData.placeType,
        note: formData.note,
        name: formData.name,
        url: formData.url || null,
        photos: photos,
        price_range: formData.priceRange || null,
        requires_reservation: formData.requiresReservation,
        cuisine_type: formData.cuisineType || null,
        amenities: formData.amenities ? formData.amenities.split(',').map((item: string) => item.trim()) : null,
        accessibility_features: formData.accessibilityFeatures ? formData.accessibilityFeatures.split(',').map((item: string) => item.trim()) : null,
        best_time_to_visit: formData.bestTimeToVisit || null,
        tags: formData.tags ? formData.tags.split(',').map((item: string) => item.trim()) : null,
        rating: formData.rating ? parseFloat(formData.rating) : null,
        rating_count: formData.rating ? 1 : 0,
        address: formData.address || null,
        city: playlist?.city || formData.city || null,
        country: playlist?.country || formData.country || null,
        created_by_user_id: session.user.id,
        dress_code: formData.dressCode || null,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        family_friendly: formData.familyFriendly,
        has_food: formData.hasFood,
        categories: formData.categories ? formData.categories.split(',').map((item: string) => item.trim()) : null,
        start_date: formData.startDate || null,
        end_date: formData.endDate || null,
        frequency: formData.frequency || null,
        ticket_price: formData.ticketPrice || null,
        original_place_id: originalPlaceId
      };

      // Append attraction details to note if they exist
      if (formData.placeType === 'attraction' && 
         (formData.entranceFee || formData.attractionType || formData.suggestedDuration || 
          formData.exhibits || formData.historicalSignificance)) {
        
        const attractionDetails = [];
        if (formData.attractionType) attractionDetails.push(`Type: ${formData.attractionType}`);
        if (formData.entranceFee) attractionDetails.push(`Entrance fee: ${formData.entranceFee}`);
        if (formData.suggestedDuration) attractionDetails.push(`Suggested duration: ${formData.suggestedDuration} hours`);
        if (formData.isIndoor !== undefined) attractionDetails.push(`Indoor attraction: ${formData.isIndoor ? 'Yes' : 'No'}`);
        
        const attractionNote = attractionDetails.join('\n');
        
        // Add exhibits and historical significance to separate paragraphs
        const fullNote = [
          placeData.note || '',
          attractionNote,
          formData.exhibits ? `Exhibits: ${formData.exhibits}` : '',
          formData.historicalSignificance ? `Historical significance: ${formData.historicalSignificance}` : ''
        ].filter(Boolean).join('\n\n');
        
        placeData.note = fullNote.trim() || null;
      }

      const { error } = await supabase
        .from('destination_playlist_items')
        .insert(placeData);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Place added to collection",
      });

      queryClient.invalidateQueries({ queryKey: ['playlist-items'] });
      onClose();
      return true;
    } catch (error) {
      console.error('Error adding place:', error);
      toast({
        title: "Error",
        description: "Failed to add place" + (error instanceof Error ? `: ${error.message}` : ''),
        variant: "destructive",
      });
      return false;
    }
  };

  return { submitForm };
}
